/**
 * iter256 — Onboarding Section Setup modal.
 *
 * Single component that handles all 6 auto-computed pre-opening
 * sections (staff / pos / aggregator / compliance / premises /
 * preopen).  Statuses stay auto-computed on the Dashboard — this
 * modal is where the manager enters the actual data that DRIVES
 * those statuses.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Field specs per section ─────────────────────────────────
const SPEC = {
  staff_mapping: {
    title: 'Setup Staff Mapping',
    endpoint: 'staff', collectionLabel: 'Staff',
    columns: [
      { key: 'role', label: 'Role *', kind: 'select', options: ['Manager', 'Chef', 'Service Staff', 'Cashier', 'Housekeeping', 'Delivery Partner'], required: true },
      { key: 'name', label: 'Name *', required: true },
      { key: 'employee_id', label: 'Employee ID' },
      { key: 'mobile', label: 'Mobile' },
      { key: 'email', label: 'Email' },
      { key: 'joining_date', label: 'Joining Date', kind: 'date' },
      { key: 'salary', label: 'Salary', kind: 'number' },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'Required opening roles: Manager × 1, Chef × 1, Service Staff × 2. Save each staff member below; the Dashboard auto-updates.',
  },
  pos_mapping: {
    title: 'Setup POS Configuration',
    endpoint: 'pos', collectionLabel: 'POS',
    columns: [
      { key: 'provider', label: 'POS Provider *', required: true, placeholder: 'e.g. Petpooja, PosBytz, Torqus' },
      { key: 'pos_code', label: 'POS Code *', required: true },
      { key: 'billing_code', label: 'Billing Code *', required: true },
      { key: 'outlet_id', label: 'Outlet / Restaurant ID' },
      { key: 'mobile', label: 'Registered Mobile' },
      { key: 'email', label: 'Registered Email' },
      { key: 'activation_status', label: 'Activation Status', kind: 'select', options: ['Pending', 'Live', 'Suspended'] },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'POS Code + Billing Code are mandatory. Section flips to COMPLETED once both are saved on any row.',
  },
  aggregator_mapping: {
    title: 'Setup Aggregators',
    endpoint: 'aggregator', collectionLabel: 'Aggregator',
    columns: [
      { key: 'aggregator_name', label: 'Aggregator *', kind: 'select', options: ['Swiggy', 'Zomato', 'UberEats', 'DoorDash', 'Talabat', 'Deliveroo', 'GrabFood', 'Foodpanda', 'Other'], required: true },
      { key: 'outlet_id', label: 'Outlet ID' },
      { key: 'mobile', label: 'Registered Mobile' },
      { key: 'email', label: 'Registered Email' },
      { key: 'commission_pct', label: 'Commission %', kind: 'number' },
      { key: 'gst_pct', label: 'GST %', kind: 'number' },
      { key: 'status', label: 'Status', kind: 'select', options: ['Pending', 'Active', 'Suspended', 'Not Applicable'] },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'Required aggregators come from franchise requirement (India defaults: Swiggy + Zomato). NOTE: passwords are NEVER stored here.',
  },
  compliance: {
    title: 'Setup Compliance Documents',
    endpoint: 'compliance', collectionLabel: 'Compliance Item',
    columns: [
      { key: 'licence_name', label: 'Licence / Document *', kind: 'select', options: ['FSSAI', 'GST Registration', 'Shop & Establishment', 'Fire NOC', 'Trade Licence', 'Municipal Licence', 'Labour Registration', 'Liquor Licence', 'Health / Sanitary', 'Other'], required: true },
      { key: 'mandatory', label: 'Mandatory?', kind: 'select', options: ['Yes', 'No'], default: 'Yes' },
      { key: 'status', label: 'Status *', kind: 'select', options: ['PENDING', 'RECEIVED', 'VERIFIED', 'NOT_APPLICABLE'], required: true, default: 'PENDING' },
      { key: 'licence_number', label: 'Number' },
      { key: 'issue_date', label: 'Issue Date', kind: 'date' },
      { key: 'expiry_date', label: 'Expiry Date', kind: 'date' },
      { key: 'verified_by', label: 'Verified By' },
      { key: 'verified_date', label: 'Verified Date', kind: 'date' },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'All mandatory rows must be VERIFIED / RECEIVED / NOT_APPLICABLE for section to become COMPLETED.',
  },
  premises_condition: {
    title: 'Premises Condition Checklist',
    endpoint: 'premises-condition', collectionLabel: 'Premises Check',
    columns: [
      { key: 'label', label: 'Area / Item *', kind: 'select', options: ['Premises / Civil', 'Kitchen', 'Electrical', 'Plumbing', 'Fire & Safety', 'Dining / Customer Area', 'Signage', 'Storage', 'Other'], required: true },
      { key: 'mandatory', label: 'Mandatory?', kind: 'select', options: ['Yes', 'No'], default: 'Yes' },
      { key: 'status', label: 'Status *', kind: 'select', options: ['PENDING', 'OK', 'NA'], required: true, default: 'PENDING' },
      { key: 'checked_by', label: 'Checked By' },
      { key: 'checked_date', label: 'Checked Date', kind: 'date' },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'All mandatory areas must be OK or NA for section to become COMPLETED.',
  },
  pre_opening_tasks: {
    title: 'Pre-Opening Tasks',
    endpoint: 'preopening', collectionLabel: 'Task',
    columns: [
      { key: 'label', label: 'Task *', kind: 'select', options: ['Deep Cleaning Completed', 'Equipment Installed / Tested', 'Menu / POS Configured', 'Opening Stock Received', 'Staff Training Completed', 'Branding / Signage Completed', 'Trial Run Completed', 'Vendor Readiness', 'Uniform Ready', 'Other'], required: true },
      { key: 'mandatory', label: 'Mandatory?', kind: 'select', options: ['Yes', 'No'], default: 'Yes' },
      { key: 'status', label: 'Status *', kind: 'select', options: ['PENDING', 'DONE', 'NA'], required: true, default: 'PENDING' },
      { key: 'responsible', label: 'Responsible Person' },
      { key: 'target_date', label: 'Target Date', kind: 'date' },
      { key: 'completed_date', label: 'Completed Date', kind: 'date' },
      { key: 'remarks', label: 'Remarks', kind: 'textarea' },
    ],
    hint: 'All mandatory tasks must be DONE or NA for section to become COMPLETED.',
  },
};

// ── Reusable row form ────────────────────────────────────────
function RowForm({ spec, initial, onSubmit, onCancel, busy }) {
  const [f, setF] = useState(() => {
    const base = {};
    spec.columns.forEach(c => { base[c.key] = initial?.[c.key] ?? (c.default || ''); });
    return base;
  });
  const patch = (k, v) => setF(x => ({ ...x, [k]: v }));
  const render = (c) => {
    if (c.kind === 'select') return (
      <Select value={String(f[c.key] || '')} onValueChange={v => patch(c.key, v)}>
        <SelectTrigger data-testid={`ss-${c.key}`}><SelectValue placeholder={c.label} /></SelectTrigger>
        <SelectContent>{c.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>);
    if (c.kind === 'textarea') return <Textarea rows={2} value={f[c.key] || ''} onChange={e => patch(c.key, e.target.value)} data-testid={`ss-${c.key}`} />;
    return <Input type={c.kind === 'number' ? 'number' : c.kind === 'date' ? 'date' : 'text'} value={f[c.key] || ''} onChange={e => patch(c.key, e.target.value)} placeholder={c.placeholder || ''} data-testid={`ss-${c.key}`} />;
  };
  const submit = () => {
    for (const c of spec.columns) {
      if (c.required && !String(f[c.key] ?? '').trim()) return toast.error(`${c.label} required`);
    }
    // normalise mandatory Yes/No → boolean
    const payload = { ...f };
    if ('mandatory' in payload) payload.mandatory = payload.mandatory !== 'No';
    onSubmit(payload);
  };
  return (
    <div className="border rounded p-3 bg-muted/10 space-y-3" data-testid="ss-row-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {spec.columns.map(c => (
          <div key={c.key}>
            <Label className="text-[11px]">{c.label}</Label>
            {render(c)}
          </div>
        ))}
      </div>
      <div className="flex gap-2 justify-end">
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid="ss-cancel">Cancel</Button>}
        <Button size="sm" onClick={submit} disabled={busy} data-testid="ss-submit"><Save className="w-3 h-3 mr-1" />{busy ? 'Saving…' : 'Save'}</Button>
      </div>
    </div>
  );
}

export function SectionSetupModal({ open, sectionKey, onClose, onboardingId, token, onSaved }) {
  const spec = SPEC[sectionKey];
  const [rows, setRows] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!spec || !onboardingId) return;
    const r = await fetch(`${API}/api/franchise-onboarding/${spec.endpoint}/list`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, onboarding_id: onboardingId }),
    }).then(r => r.json()).catch(() => ({ items: [] }));
    setRows(r.items || []);
  }, [spec, onboardingId, token]);

  useEffect(() => { if (open) { setShowAdd(false); setEditing(null); refresh(); } }, [open, sectionKey, refresh]);

  if (!spec) return null;

  const save = async (payload) => {
    setBusy(true);
    try {
      const body = { token, onboarding_id: onboardingId, id: editing?.id || null, payload };
      const res = await fetch(`${API}/api/franchise-onboarding/${spec.endpoint}/upsert`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Save failed');
      toast.success('Saved');
      setEditing(null); setShowAdd(false);
      await refresh(); onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/${spec.endpoint}/delete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, onboarding_id: onboardingId, id }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Delete failed');
      await refresh(); onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid={`section-setup-modal-${sectionKey}`}>
        <DialogHeader><DialogTitle>{spec.title}</DialogTitle></DialogHeader>
        <div className="text-[11px] text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2">{spec.hint}</div>

        {/* Existing rows */}
        <div className="border rounded overflow-x-auto max-h-72">
          <table className="w-full text-xs" data-testid="ss-list-table">
            <thead className="bg-muted/40 sticky top-0">
              <tr className="text-left border-b">
                {spec.columns.slice(0, 5).map(c => <th key={c.key} className="p-2">{c.label.replace(' *', '')}</th>)}
                <th className="p-2 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-b" data-testid={`ss-row-${r.id}`}>
                  {spec.columns.slice(0, 5).map(c => (
                    <td key={c.key} className="p-2">
                      {c.key === 'status' && r[c.key]
                        ? <Badge variant="outline" className="text-[10px]">{String(r[c.key])}</Badge>
                        : (r[c.key] === true ? 'Yes' : r[c.key] === false ? 'No' : (r[c.key] || '—'))}
                    </td>
                  ))}
                  <td className="p-2">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => { setEditing(r); setShowAdd(true); }} data-testid={`ss-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => del(r.id)} data-testid={`ss-del-${r.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-muted-foreground text-xs">No entries yet. Click "Add" to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {!showAdd ? (
          <Button size="sm" onClick={() => { setEditing(null); setShowAdd(true); }} data-testid="ss-add-btn"><Plus className="w-3 h-3 mr-1" />Add {spec.collectionLabel}</Button>
        ) : (
          <RowForm spec={spec} initial={editing} busy={busy}
                     onSubmit={save} onCancel={() => { setShowAdd(false); setEditing(null); }} />
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SectionSetupModal;
